/**
 * Calculation Utilities for Dashboard V2 Widgets
 * 
 * These functions provide reusable calculation logic for all dashboard widgets.
 * All calculations handle missing data gracefully by returning null or excluding clients.
 */

import { ClientType } from "../../../types";

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth: string | undefined | null): number | null {
  if (!dateOfBirth) return null;
  
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  } catch (error) {
    console.error("Error calculating age:", error);
    return null;
  }
}

/**
 * Calculate annual retirement need
 * Uses precomputed value if available, otherwise calculates from gross income and percentage
 */
export function calculateRetirementNeed(
  grossIncome: number | undefined,
  neededAsPercentage: number | undefined,
  precomputedNeed?: number | null
): number | null {
  // Use precomputed value if available
  if (precomputedNeed !== undefined && precomputedNeed !== null && precomputedNeed > 0) {
    return precomputedNeed;
  }
  
  // Calculate from gross income and percentage
  if (!grossIncome || !neededAsPercentage) return null;
  return grossIncome * (neededAsPercentage / 100);
}

/**
 * Get projected TSP balance
 * TODO: This should use precomputed value if available, or calculate based on projection logic
 */
export function getProjectedTSPBalance(client: ClientType): number | null {
  // Check for precomputed projected balance first
  if ((client as any).projectedTSPBalance !== undefined) {
    return (client as any).projectedTSPBalance;
  }
  
  // Fallback: Use current balance if available
  // In production, this should use actual projection logic
  const currentBalance = client.pretax_currentBalance || 
                        client.pretaxCurrentBalance || 
                        (client as any).tsp_currentBalance;
  
  return currentBalance || null;
}

/**
 * Get projected 401(k) balance
 * Uses final value in projection timeline (balance at retirement)
 */
export function getProjected401kBalance(client: ClientType): number | null {
  // Check for precomputed projected balance
  if ((client as any).projected401kBalance !== undefined) {
    return (client as any).projected401kBalance;
  }
  
  // Check for existing 401(k) value
  const existingValue = client.t_401k_existing401kValue;
  return existingValue || null;
}

/**
 * Get projected 403(b) balance
 * Uses current or projected balance
 */
export function getProjected403bBalance(client: ClientType): number | null {
  // Check for precomputed projected balance
  if ((client as any).projected403bBalance !== undefined) {
    return (client as any).projected403bBalance;
  }
  
  // Use current balance
  const currentBalance = client.t_403b_currentBalance;
  return currentBalance || null;
}

/**
 * Get annuity or rollover account value
 */
export function getAnnuityValue(client: ClientType): number | null {
  // Check for annuity value field
  if ((client as any).annuityValue !== undefined) {
    return (client as any).annuityValue;
  }
  
  // Check for rollover value
  if ((client as any).rolloverValue !== undefined) {
    return (client as any).rolloverValue;
  }
  
  return null;
}

/**
 * Get annual TSP income
 * Uses precomputed value if available, otherwise uses 4% rule on projected balance
 */
export function getAnnualTSPIncome(client: ClientType): number | null {
  // Check for precomputed annual income first
  if ((client as any).annualTSPIncome !== undefined && (client as any).annualTSPIncome !== null) {
    return (client as any).annualTSPIncome;
  }
  
  // Use 4% rule on projected balance as fallback
  const balance = getProjectedTSPBalance(client);
  return balance ? balance * 0.04 : null;
}

/**
 * Get annual 401(k) income
 * Uses precomputed value if available, otherwise uses 4% rule
 */
export function getAnnual401kIncome(client: ClientType): number | null {
  if ((client as any).annual401kIncome !== undefined && (client as any).annual401kIncome !== null) {
    return (client as any).annual401kIncome;
  }
  
  const balance = getProjected401kBalance(client);
  return balance ? balance * 0.04 : null;
}

/**
 * Get annual 403(b) income
 * Uses precomputed value if available, otherwise uses 4% rule
 */
export function getAnnual403bIncome(client: ClientType): number | null {
  if ((client as any).annual403bIncome !== undefined && (client as any).annual403bIncome !== null) {
    return (client as any).annual403bIncome;
  }
  
  const balance = getProjected403bBalance(client);
  return balance ? balance * 0.04 : null;
}

/**
 * Get annual Social Security income
 */
export function getAnnualSSIncome(client: ClientType): number | null {
  // Check for precomputed annual SS income
  if ((client as any).annualSSIncome !== undefined && (client as any).annualSSIncome !== null) {
    return (client as any).annualSSIncome;
  }
  
  // Check for monthly income and convert to annual
  if (client.monthlyIncomeAt62) {
    return client.monthlyIncomeAt62 * 12;
  }
  
  // Check for other SS income fields
  if ((client as any).socialSecurityAnnualIncome) {
    return (client as any).socialSecurityAnnualIncome;
  }
  
  return null;
}

/**
 * Get annual annuity income
 */
export function getAnnualAnnuityIncome(client: ClientType): number | null {
  if ((client as any).annualAnnuityIncome !== undefined && (client as any).annualAnnuityIncome !== null) {
    return (client as any).annualAnnuityIncome;
  }
  
  // Use 4% rule on annuity value as fallback
  const annuityValue = getAnnuityValue(client);
  return annuityValue ? annuityValue * 0.04 : null;
}

/**
 * Get other retirement income sources
 */
export function getOtherRetirementIncome(client: ClientType): number | null {
  // Check for additional income field
  if (client.additionalIncome) {
    try {
      const additionalIncome = typeof client.additionalIncome === 'string' 
        ? JSON.parse(client.additionalIncome) 
        : client.additionalIncome;
      
      if (typeof additionalIncome === 'object' && additionalIncome !== null) {
        // Sum all additional income sources
        const total = Object.values(additionalIncome).reduce((sum: number, val: any) => {
          const num = typeof val === 'number' ? val : parseFloat(val) || 0;
          return sum + num;
        }, 0);
        return total > 0 ? total : null;
      }
    } catch (error) {
      console.error("Error parsing additional income:", error);
    }
  }
  
  return null;
}

/**
 * Calculate projected annual retirement income (sum of all sources)
 */
export function calculateProjectedRetirementIncome(client: ClientType): number {
  let total = 0;
  
  total += getAnnualTSPIncome(client) || 0;
  total += getAnnual401kIncome(client) || 0;
  total += getAnnual403bIncome(client) || 0;
  total += getAnnualSSIncome(client) || 0;
  total += getAnnualAnnuityIncome(client) || 0;
  total += getOtherRetirementIncome(client) || 0;
  
  return total;
}

/**
 * Calculate retirement readiness ratio
 * Returns ratio of projected income to retirement need
 */
export function calculateReadinessRatio(client: ClientType): number | null {
  const retirementNeed = calculateRetirementNeed(
    client.grossIncome,
    client.neededAsPercentage,
    (client as any).retirementNeed // Precomputed if available
  );
  
  if (!retirementNeed || retirementNeed === 0) return null;
  
  const projectedIncome = calculateProjectedRetirementIncome(client);
  if (!projectedIncome || projectedIncome === 0) return null;
  
  return projectedIncome / retirementNeed;
}

/**
 * Calculate TSP contribution rate as percentage of income
 */
export function calculateTSPContributionRate(client: ClientType): number | null {
  if (!client.pretax_contributionPerPaycheck || 
      !client.payPeriodsAnnual || 
      !client.grossIncome) {
    return null;
  }
  
  const annualContribution = client.pretax_contributionPerPaycheck * client.payPeriodsAnnual;
  return (annualContribution / client.grossIncome) * 100;
}

/**
 * Calculate FEGLI coverage reduction percentage
 * Returns precomputed value if available, otherwise calculates from FEGLI options
 */
export function calculateFEGLIReduction(client: ClientType): number | null {
  // Check for precomputed reduction percentage
  if ((client as any).fegliReductionPercentage !== undefined && (client as any).fegliReductionPercentage !== null) {
    console.log(`🔍 calculateFEGLIReduction: Client ${client.id} using precomputed reduction:`, (client as any).fegliReductionPercentage);
    return (client as any).fegliReductionPercentage;
  }
  
  // Calculate reduction based on FEGLI options and reduction settings
  let totalReduction = 0;
  
  // Helper function to parse reduction value from various formats
  const parseReductionValue = (value: any): number | null => {
    if (!value) return null;
    
    // Handle number directly
    if (typeof value === 'number') {
      return isNaN(value) ? null : value;
    }
    
    // Handle string formats
    if (typeof value === 'string') {
      // Handle "50_percent_reduction" or "75_percent_reduction" format
      if (value.includes('50_percent')) {
        return 50;
      }
      if (value.includes('75_percent')) {
        return 75;
      }
      if (value === 'no_reduction' || value === 'none' || value === '') {
        return 0; // No reduction = 0% reduction (low risk)
      }
      
      // Handle percentage string like "50%" or "75%"
      const cleaned = value.replace('%', '').trim();
      const parsed = parseFloat(cleaned);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
    
    return null;
  };
  
  // Helper to check if a value is truthy (handles boolean, string "true", 1, etc.)
  const isTruthy = (value: any): boolean => {
    if (value === true || value === 1 || value === "1") return true;
    if (typeof value === 'string' && value.toLowerCase() === 'true') return true;
    return false;
  };
  
  // Check Option B reduction
  if (isTruthy(client.fegli_optionB)) {
    const optionBReduction = (client as any).fegli_optionBAge65Reduction;
    const reductionValue = parseReductionValue(optionBReduction);
    if (reductionValue !== null) {
      totalReduction = Math.max(totalReduction, reductionValue);
    }
  }
  
  // Check Option C reduction
  if (isTruthy(client.fegli_optionC)) {
    const optionCReduction = (client as any).fegli_optionCAge65Reduction;
    const reductionValue = parseReductionValue(optionCReduction);
    if (reductionValue !== null) {
      totalReduction = Math.max(totalReduction, reductionValue);
    }
  }
  
  // Check general reduction at age 65
  const reductionAtAge65 = (client as any).fegli_reductionAtAge65;
  const reductionValue = parseReductionValue(reductionAtAge65);
  if (reductionValue !== null) {
    totalReduction = Math.max(totalReduction, reductionValue);
  }
  
  // If client has FEGLI coverage but no reduction settings found, return 0 (low risk)
  // This allows clients with FEGLI coverage to be counted even if they don't have reduction settings
  // Only return null if we truly can't determine anything about the client's FEGLI status
  const hasFEGLICoverage = isTruthy(client.fegli_basicCoverage) || 
                           isTruthy(client.fegli_optionA) || 
                           isTruthy(client.fegli_optionB) || 
                           isTruthy(client.fegli_optionC);
  
  // Check if client has any coverage fields defined (even if false)
  const hasCoverageFieldsDefined = (
    client.fegli_basicCoverage !== undefined ||
    client.fegli_optionA !== undefined ||
    client.fegli_optionB !== undefined ||
    client.fegli_optionC !== undefined
  );
  
  // Also check if client has FEGLI reduction data (indicates they have FEGLI coverage)
  // Count if they have actual reduction values OR if they have reduction fields + coverage fields defined
  const clientAny = client as any;
  const hasFEGLIReductionData = (
    // Precomputed reduction percentage (definitely has coverage)
    (clientAny.fegliReductionPercentage !== undefined && 
     clientAny.fegliReductionPercentage !== null &&
     clientAny.fegliReductionPercentage >= 0) ||
    // Reduction fields with actual reduction values (not "no_reduction" or "none")
    (clientAny.fegli_reductionAtAge65 && 
     clientAny.fegli_reductionAtAge65 !== 'no_reduction' && 
     clientAny.fegli_reductionAtAge65 !== 'none' &&
     clientAny.fegli_reductionAtAge65 !== '') ||
    (clientAny.fegli_optionBAge65Reduction && 
     clientAny.fegli_optionBAge65Reduction !== 'no_reduction' && 
     clientAny.fegli_optionBAge65Reduction !== 'none' &&
     clientAny.fegli_optionBAge65Reduction !== '') ||
    (clientAny.fegli_optionCAge65Reduction && 
     clientAny.fegli_optionCAge65Reduction !== 'no_reduction' && 
     clientAny.fegli_optionCAge65Reduction !== 'none' &&
     clientAny.fegli_optionCAge65Reduction !== '') ||
    // If they have reduction fields populated AND coverage fields defined, count them
    // (even if reduction is "no_reduction" - they still have FEGLI data)
    (hasCoverageFieldsDefined && (
      (clientAny.fegli_reductionAtAge65 !== undefined && 
       clientAny.fegli_reductionAtAge65 !== null &&
       clientAny.fegli_reductionAtAge65 !== '') ||
      (clientAny.fegli_optionBAge65Reduction !== undefined && 
       clientAny.fegli_optionBAge65Reduction !== null &&
       clientAny.fegli_optionBAge65Reduction !== '') ||
      (clientAny.fegli_optionCAge65Reduction !== undefined && 
       clientAny.fegli_optionCAge65Reduction !== null &&
       clientAny.fegli_optionCAge65Reduction !== '')
    ))
  );
  
  if (hasFEGLICoverage || hasFEGLIReductionData) {
    // Client has FEGLI coverage (either explicit flags or reduction data) - return calculated reduction (0 if no reduction settings)
    console.log(`🔍 calculateFEGLIReduction: Client ${client.id} has FEGLI coverage, reduction:`, totalReduction, {
      hasExplicitCoverage: hasFEGLICoverage,
      hasReductionData: hasFEGLIReductionData,
      fegli_basicCoverage: client.fegli_basicCoverage,
      fegli_optionA: client.fegli_optionA,
      fegli_optionB: client.fegli_optionB,
      fegli_optionC: client.fegli_optionC,
      optionBReduction: clientAny.fegli_optionBAge65Reduction,
      optionCReduction: clientAny.fegli_optionCAge65Reduction,
      generalReduction: clientAny.fegli_reductionAtAge65,
    });
    return totalReduction;
  }
  
  // No FEGLI coverage at all - return null to skip this client
  console.log(`🔍 calculateFEGLIReduction: Client ${client.id} has NO FEGLI coverage`, {
    fegli_basicCoverage: client.fegli_basicCoverage,
    fegli_optionA: client.fegli_optionA,
    fegli_optionB: client.fegli_optionB,
    fegli_optionC: client.fegli_optionC,
  });
  return null;
}

/**
 * Calculate rollover value
 * Sum of existing 401(k) rollover value, 403(b) current balance, and annuity-eligible amounts
 * Only for 401k and 403b clients (rollover opportunities)
 */
export function getRolloverValue(client: ClientType): number {
  let rolloverValue = 0;
  
  // Only calculate for 401k and 403b clients (these are rollover opportunities)
  if (client.type === "401k") {
    // For 401k clients: existing 401k value + annuity value
    const existing401kValue = client.t_401k_existing401kValue || 0;
    const annuityEligible = getAnnuityValue(client) || 0;
    rolloverValue = existing401kValue + annuityEligible;
  } else if (client.type === "403b") {
    // For 403b clients: current 403b balance + annuity value
    const current403bBalance = client.t_403b_currentBalance || 0;
    const annuityEligible = getAnnuityValue(client) || 0;
    rolloverValue = current403bBalance + annuityEligible;
  }
  // For federal clients or other types, return 0 (no rollover opportunity)
  
  return rolloverValue;
}

/**
 * Calculate employer match status
 * Returns: 'noMatch' | 'underMatch' | 'atOrAboveMatch' | null
 */
export function calculateEmployerMatchStatus(client: ClientType): {
  status: 'noMatch' | 'underMatch' | 'atOrAboveMatch';
  matchRate: number;
  contributionRate: number;
} | null {
  let matchRate: number | null = null;
  let contributionRate: number | null = null;
  
  // Check 401(k)
  if (client.t_401k_employerMatchPercent !== undefined && 
      client.t_401k_employerMatchPercent !== null) {
    matchRate = client.t_401k_employerMatchPercent;
    contributionRate = client.t_401k_monthlyContributionPercent || null;
  }
  // Check 403(b)
  else if (client.t_403b_employerMatchPercentage !== undefined && 
           client.t_403b_employerMatchPercentage !== null) {
    matchRate = client.t_403b_employerMatchPercentage;
    contributionRate = client.t_403b_contributionPercentage || null;
  }
  
  if (matchRate === null || matchRate === 0) {
    return {
      status: 'noMatch',
      matchRate: 0,
      contributionRate: contributionRate || 0
    };
  }
  
  if (contributionRate === null) {
    return {
      status: 'noMatch', // Unknown, treat as no match
      matchRate,
      contributionRate: 0
    };
  }
  
  if (contributionRate < matchRate) {
    return {
      status: 'underMatch',
      matchRate,
      contributionRate
    };
  }
  
  return {
    status: 'atOrAboveMatch',
    matchRate,
    contributionRate
  };
}

/**
 * Get annuity-eligible amount for rollover calculations
 */
export function getAnnuityEligibleAmount(client: ClientType): number | null {
  return getAnnuityValue(client);
}

